import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild,
} from '@angular/core';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { Socket } from 'socket.io-client';
import { AnalyticsService } from '../../@core/utils';
import { OpenAICallRestriction } from '../../enum/openai_restrictions';
import { UserRoles } from '../../enum/roles.enum';
import { ClearObservableHelper } from '../../helpers/ClearObservableHelper';
import { User, UserData } from '../../interface/user';
import { AuthService } from '../../services/auth.service';
import { OpenAiAssistantService } from '../../services/open-ai-assistant.service';
import { StorageService } from '../../services/storage.service';
import { ToasterService } from '../../services/toaster.service';
import { UtilService } from '../../services/util.service';
import { MatDialogComponent } from '../mat-dialog/mat-dialog.component';

@Component({
  selector: 'ngx-global-assistant',
  templateUrl: './global-assistant.component.html',
  styleUrl: './global-assistant.component.scss',
})
export class GlobalAssistantComponent extends ClearObservableHelper implements OnInit, OnDestroy, OnChanges {
  @Input() currentUser: User;
  @Input() isExpand = false;
  @Input() isCardView = false;
  @Output() removeQuestion = new EventEmitter<boolean>();
  @Output() resetQuestion = new EventEmitter<boolean>();
  @ViewChild('dialogDelete') dialogDelete: TemplateRef<any>;
  public question: string = '';
  public answer: any = '';
  private count = 0;
  private isRecordExist;
  public searchText: string = '';
  private threadId: string = '';
  public currentQuestion: string = '';
  public loading: boolean = false;
  public pageTotal: number = 1;
  public aiAssistantRecords = [];
  public localStorageUser: UserData;
  public currentPage = 1;
  private currentAssistant;
  userSchoolId: number;
  currentSelectedSchool;
  private currentDeleteId: number;
  socket: Socket;
  currentUrl: string;
  userAccessToken;
  userAccessAIToken: any;

  constructor(
    private toasterService: ToasterService,
    private authService: AuthService,
    private openAIAssitantService: OpenAiAssistantService,
    private route: ActivatedRoute,
    private storageService: StorageService,
    public router: Router,
    public dialog: MatDialog,
    public analyticsService: AnalyticsService,
    public dialogRef: MatDialogRef<any>,
    public spinnerService: NgxSpinnerService,
    private utilService: UtilService,
  ) {
    super();
  }

  async ngOnInit() {
    this.spinnerService.show();
    this.localStorageUser = this.storageService.getItem('user');

    this.currentUrl = this.router.url;

    this.currentSelectedSchool = this.storageService.getItem('selectedSchoolByAdmin');
    this.userSchoolId = Number(this.route.snapshot.paramMap.get('schoolId'));
    await this.getAIAssistantRecordsPerUserId();

    this.storageService.submitAssitantQuestion.subscribe(async (x) => {
      try {
        this.spinnerService.show();
        this.currentQuestion = x;
        await this.getAIResponse();
        this.spinnerService.hide();
      } catch (error) {
        this.spinnerService.hide();
      }
    });

    this.authService.closeDialog.subscribe(async (res) => {
      this.resetAssistantResponse();

      if (res) await this.getAIAssistantRecordsPerUserId();
    });
    this.spinnerService.hide();
    // this.getUserToken();
    this.getUserAIToken();
  }

  private isProcessingAIResponse = false;
  public async getAIResponse() {
    if (this.isProcessingAIResponse) {
      return;
    }
    this.isProcessingAIResponse = true;
    try {
      this.spinnerService.show();
      if (this.currentQuestion !== '') {
        if (this.aiAssistantRecords.length > 0) {
          this.isRecordExist = this.aiAssistantRecords.find(
            (x) => x.message.trim().toLowerCase() === this.currentQuestion.trim().toLowerCase(),
          );
          if (this.isRecordExist?.id) {
            this.spinnerService.hide();
            this.toasterService.showToast('This query already exists.', 'danger');
            this.question = this.isRecordExist.message;
            this.answer = this.formatText(this.isRecordExist.response);
            this.removeQuestion.emit(true);
            this.resetQuestion.emit(false);
          } else await this.createThreadMessage();
        } else {
          await this.createThreadMessage();
        }
      } else {
        this.spinnerService.hide();
        this.toasterService.showToast('Question should not be empty', 'danger');
      }
      this.spinnerService.hide();
    } catch (error) {
      // console.log(error);
    } finally {
      this.isProcessingAIResponse = false;
    }
  }

  private async createThreadMessage() {
    try {
      this.spinnerService.show();
      // this.utilService.showLoading();
      // restrict openAI calls
      if (!this.utilService.checkIfTokenLimitExceededAndCreditsOut(this.userAccessAIToken, this.currentUser)) {
        this.resetQuestion.emit(true);
        await this.createThreadMessageUsingNodeServer();
        // this.spinnerService.hide();
        // this.utilService.hideLoading();
        // restrict openAI calls
        // Handle AI token usage centrally for teachers, trial users, and limited-access users
        await this.handleAITokenUsage(this.localStorageUser?.user, this.userAccessAIToken);
      } else {
        this.spinnerService.hide();
        this.utilService.hideLoading();
      }
    } catch (error) {
      this.loading = false;
      this.spinnerService.hide();
      this.utilService.hideLoading();
      // console.log(error);
    }
  }

  public async addRecordToDatabase(aiRes) {
    try {
      this.spinnerService.show();
      // this.utilService.showLoading();
      const response = await this.openAIAssitantService.addOpenAiAssistant(aiRes);

      if (response['id']) {
        this.utilService.hideLoading();
        // this.spinnerService.hide();
        this.currentAssistant = response;
        this.utilService.questionAsked = true;
        await this.getAIAssistantRecordsPerUserId();
      }
      this.utilService.hideLoading();
      // this.spinnerService.hide();
    } catch (error) {
      this.utilService.hideLoading();
      // console.log(error)
    }
  }

  private async getAIAssistantRecordsPerUserId(isPagination = false) {
    try {
      this.localStorageUser?.user?.role_id === 19
        ? Number(this.route.snapshot.paramMap.get('id'))
        : this.currentUser?.id;

      const params = {
        user_id:
          this.localStorageUser?.user?.role_id === 19
            ? Number(this.route.snapshot.paramMap.get('id'))
            : this.currentUser?.id,
        page: this.currentPage,
      };

      const res = await this.openAIAssitantService.getOpenAIAssistant(params);
      this.pageTotal = res['pageTotal'];
      this.aiAssistantRecords = isPagination ? [...this.aiAssistantRecords, ...res['items']] : res['items'];

      if (this.aiAssistantRecords?.length > 0 && this.localStorageUser?.user?.role_id === 19) {
        this.question = this.aiAssistantRecords[0]?.message;
        this.answer = this.aiAssistantRecords[0]?.response;
      }
    } catch (error) {
      // console.log(error);
    }
  }

  private async createThreadMessageUsingNodeServer(isUpdate = false) {
    try {
      this.spinnerService.show();
      this.loading = true;
      let message_id = '';
      let question = '';
      const body = {
        message: this.currentQuestion,
        thread_id: isUpdate ? this.currentAssistant?.thread_id : this.threadId,
        status: isUpdate ? 'update' : this.count > 0 ? 'update' : 'add',
        userId: this.localStorageUser.user.id,
      };
      this.currentQuestion = '';
      this.answer = '';

      return await (await this.openAIAssitantService.createThreadMessage(body)).subscribe({
        next: async (aiRes: any) => {
          // this.resetQuestion.emit(false);
          this.spinnerService.hide();
          this.loading = false;
          this.question = aiRes.question; // Store the original question

          if (aiRes.answer) {
            this.answer += aiRes.answer; // Append the AI response chunk
            this.answer = this.formatText(this.answer);
          }
          if (aiRes.done) {
            body.thread_id = aiRes.response.thread_id;
            message_id = aiRes.response.message_id;
            question = aiRes.response.question;
          }
          this.utilService.hideLoading();
        },
        error: (error) => {
          this.utilService.hideLoading();
          console.error('Error receiving stream:', error);
          this.toasterService.showToast(
            'Our AI Assistant not working currently, Please try again after some time',
            'danger',
          );
          this.spinnerService.hide();
          this.loading = false;
        },
        complete: async () => {
          this.spinnerService.hide();
          this.utilService.hideLoading();
          this.loading = false;
          this.question = question;
          this.removeQuestion.emit(true);
          this.resetQuestion.emit(false);
          await this.addRecordToDatabase({
            thread_id: body?.thread_id, // Ensure thread_id is correctly passed
            message_id: message_id, // Ensure message_id is passed
            message: question,
            response: this.answer,
          });
          await this.spinnerService.hide();
        },
      });
    } catch (error) {
      this.utilService.hideLoading();
      // console.log(error)
    }
  }

  public async regenerateResponse() {
    try {
      this.loading = true;
      await this.createThreadMessageUsingNodeServer(true);
      // if (response?.code === 200) {
      //   this.answer = response?.data?.response;
      //   this.threadId = response?.data?.thread_id;
      //   this.loading = false;
      //   await this.updateAIAssistantFromDatabase(response?.data);
      //   this.removeQuestion.emit(true);
      // } else {
      //   this.loading = false;

      //   this.toasterService.showToast(
      //     'Our AI Assistant not working currently, Please try again after some time',
      //     'danger',
      //   );
      // }
    } catch (error) {
      this.loading = false;
      // console.log(error);
    }
  }

  private async updateAIAssistantFromDatabase(aiRes) {
    try {
      const params = {
        openai_assistant_id: this.currentAssistant?.id,
        thread_id: this.currentAssistant?.thread_id,
        message_id: this.currentAssistant?.message_id,
        message: this.currentAssistant?.message,
        response: aiRes?.response,
      };

      const response = await this.openAIAssitantService.updateOpenAIAssistant(this.currentAssistant?.id, params);

      if (response['id']) {
        this.currentAssistant = response;
        await this.getAIAssistantRecordsPerUserId();
      }
    } catch (error) {
      // console.log(error)
    }
  }

  formatText(text: string): string {
    return text.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
  }

  public async showAssistantRecord(event, assistantRecord) {
    try {
      this.currentAssistant = assistantRecord;
      this.question = assistantRecord?.message;
      this.answer = this.formatText(assistantRecord?.response);
    } catch (error) {
      // console.log(error)
    }
  }

  public closeDialog(): void {
    try {
      this.authService.closeDialog.emit(true);
    } catch (error) {
      // console.log(error);
    }
  }

  public async getRecordsPerPage() {
    try {
      if (this.currentPage === this.pageTotal) {
        this.toasterService.showToast('No Records Found', 'danger');
      } else {
        this.currentPage++;
        await this.getAIAssistantRecordsPerUserId(true);
      }
    } catch (error) {
      // console.log(error)
    }
  }

  openDeleteModal(id: number) {
    try {
      this.currentDeleteId = id;
      // this.dialogRef = this.dialog.open(this.dialogDelete, {
      //   width: '355px',
      //   height: '270px',
      // });
      this.openConfirmDialog();
    } catch (error) {
      // console.log(error)
    }
  }

  public async deleteQuestion() {
    try {
      this.loading = true;
      await this.openAIAssitantService.deleteAiAssistant(this.currentDeleteId);
      this.resetAssistantResponse();
      await this.getAIAssistantRecordsPerUserId();
      // this.dialogRef.close();
      this.loading = false;
    } catch (error) {
      // console.log(error);
    }
  }

  resetAssistantResponse(): void {
    try {
      this.currentAssistant = null;
      this.question = '';
      this.answer = '';
      this.currentQuestion = '';
      this.resetQuestion.emit(false);
    } catch (error) {
      // console.log(error)
    }
  }

  ngOnDestroy(): void {
    if (this.socket) {
      this.socket.disconnect();
      this.socket.close();
    }
  }
  public openConfirmDialog(): void {
    const dialogRef = this.dialog.open(MatDialogComponent, {
      width: '400px',
      data: {
        title: 'Delete Question',
        icon: '/assets/icons/featured icon.svg',
        accessCodeLabel: 'Delete Question',
        confirmationMessage: 'Are you sure want to delete this question ?',
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Delete',
        cancelTrackEvent: 'Cancel Delete Question',
        confirmTrackEvent: 'Delete Question',
        trackPage: 'Question',
      },
    });
    dialogRef.afterClosed().subscribe(async (result) => {
      if (result) {
        this.deleteQuestion();
      }
    });
  }

  // async getUserToken() {
  //   try {
  //     // restrict openAI calls
  //     this.userAccessToken = await this.utilService.getUserToken(this.localStorageUser?.user);
  //   } catch (error) {
  //     console.log('GlobalAssistantComponent getUserToken', error);
  //   }
  // }

  // async updateTokenUsed() {
  //   try {
  //     // restrict openAI calls
  //     await this.utilService.updateTokenUsed(this.localStorageUser?.user, this.userAccessToken);
  //     await this.getUserToken();
  //   } catch (error) {
  //     console.log('GlobalAssistantComponent updateTokenUsed', error);
  //   }
  // }
  async ngOnChanges(changes: SimpleChanges) {
    if (changes['isExpand'] && changes['isExpand'].currentValue === true) {
      if (this.utilService.questionAsked) {
        await this.getAIAssistantRecordsPerUserId();
        if (await this.aiAssistantRecords) {
          this.question = this.aiAssistantRecords[0]?.message;
          this.answer = this.formatText(this.aiAssistantRecords[0]?.response);
        }
        this.utilService.questionAsked = false;
      } else {
        this.question = '';
        this.answer = '';
      }
    }
  }
  //AI credit Tokens
  async getUserAIToken() {
    try {
      // restrict openAI calls
      this.userAccessAIToken = await this.utilService.getUserAITokens(this.localStorageUser?.user.id);
    } catch (error) {
      // console.log('LessonPlanComponent getUserToken', error);
    }
  }
  async updateAITokenUsed() {
    try {
      // restrict openAI calls
      await this.utilService.updateUserAITokens(this.localStorageUser?.user.id, this.userAccessAIToken);

      await this.getUserAIToken();
    } catch (error) {
      // console.log('LessonPlanComponent updateTokenUsed', error);
    }
  }

  /**
   * @description Checks if a user's AI token usage should be incremented and updates it if needed.
   * Ensures that AI usage is properly counted for teachers, trial users, and users with restricted access.
   * @param user - Object containing user information (role, trial status, school limitations)
   * @param userAccessAIToken - Object containing AI token data (unlimited flag, tokens used, etc.)
   * @returns {Promise<boolean>} - Returns true if the token was updated, otherwise false
   */
  async handleAITokenUsage(user: any, userAccessAIToken: any): Promise<boolean> {
    // Convert the is_unlimited flag to string and check if the user has unlimited AI access
    const isUnlimited = String(userAccessAIToken?.is_unlimited) === 'true';
    if (isUnlimited) return false; // No token update needed for unlimited users
    // Check the user's role: teacher or teacher-only
    const isTeacher = this.storageService.checkUserRole(user?.role_id, UserRoles.Teacher);
    const isTeacherOnly = this.storageService.checkUserRole(user?.role_id, UserRoles.TeacherOnly);
    // Check if the user is a trial user
    const isTrialUser = Boolean(user?.is_trial_user);
    // Determine if the user has limited access based on their school's limitations
    // Safe fallback: if the second limitation is undefined, check the first one
    const limitedAccess = String(user?.school?.user_limitations?.[1]?.isLimitedAccess) === 'true';

    // Only increment token usage if one of the following is true:
    // - Teacher with limited access
    // - Teacher-only user
    // - Trial user
    if ((isTeacher && limitedAccess) || isTeacherOnly || isTrialUser) {
      // Call the function to update the token count in the database
      await this.updateAITokenUsed();
      return true; // Indicate that the token was updated
    }
    // Token was not updated for this user
    return false;
  }
}
